import React from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import Cookies from 'js-cookie';
import {
    useAuthRedirect,
    getResumePosition,
    trackViewingActivity,
    actionType
} from '@virtual-cinema/client-lib';
export const VideoPlayer = (props) => {
    const videoRef = React.useRef(null);
    const playerRef = React.useRef(null);
    const { options, onReady, contentId, eventId } = props;
    const [resumePosition, setResumePosition] = React.useState(0);
    const [videoCompleted, setVideoCompleted] = React.useState(false);
    const lastReportedTime = React.useRef(0);
    const accessToken = useAuthRedirect();
    const firstPlayRef = React.useRef(true);
    const [showOverlay, setShowOverlay] = React.useState(false);
    const [isInitialized, setIsInitialized] = React.useState(false);

    // Helper function to detect mobile
    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    // Fetch last position on component mount
    React.useEffect(() => {
        const fetchResumePosition = async () => {
            try {
                const { data, error } = await getResumePosition(contentId, eventId, accessToken);
                if (!error && data && data.has_progress) {
                    setResumePosition(data.position);
                    setVideoCompleted(data.completed || false);
                    console.log("[Debug] Resume position found:", data.position);
                    console.log("[Debug] Video completed:", data.completed);

                    // If we have a resume position, we're not on first play
                    if (data.position > 0) {
                        firstPlayRef.current = false;
                        setShowOverlay(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching resume position:', error);
            }
        };

        fetchResumePosition();
    }, [contentId, eventId, accessToken]);

    // Function to track video activity
    const trackActivity = async (position, actionType, duration = null) => {
        try {
            const payload = {
                content_id: contentId,
                event_id: eventId,
                position,
                action_type: actionType
            };

            if (duration !== null) {
                payload.duration = duration;
            }

            console.log(`[Debug] Tracking activity: ${actionType} at position ${position}`);
            await trackViewingActivity(payload, accessToken);
        } catch (error) {
            console.error('Error tracking video activity:', error);
        }
    };

    // Initialize player - using React state to manage initialization
    React.useEffect(() => {
        if (!playerRef.current && !isInitialized) {
            console.log("[Debug] Creating new video.js player");
            console.log("[Debug] Is mobile device:", isMobileDevice());

            const videoElement = document.createElement("video-js");
            videoElement.classList.add('vjs-big-play-centered');
            videoRef.current.appendChild(videoElement);

            videojs.use('*', function (player) {
                return {
                    setSource: function (srcObj, next) {
                        let keyPairId = Cookies.get('CloudFront-Key-Pair-Id');
                        console.log('key-pair');
                        console.log(keyPairId);
                        srcObj.withCredentials = true;
                        next(null, srcObj);
                    },
                    beforeRequest: function (options) {
                        options.withCredentials = true;
                        return options;
                    }
                };
            });

            const player = videojs(videoElement, {
                ...options,
                crossOrigin: 'use-credentials',
                autoplay: false,
                // Add enhanced mobile options
                userActions: {
                    hotkeys: true,
                    doubleClick: true
                },
                controlBar: {
                    ...options.controlBar,
                    progressControl: {
                        seekBar: true
                    }
                },
                touchEnabled: true,
                html5: {
                    nativeTextTracks: false, // Sometimes helps with mobile issues
                    hls: {
                        overrideNative: true,
                    }
                }
            }, () => {
                console.log('[Debug] Player is ready');

                // Store the player reference
                playerRef.current = player;
                setIsInitialized(true);

                // Call the onReady callback from parent component
                onReady && onReady(player);

                // Setup event listeners for audience tracking
                player.on('play', () => {
                    console.log('[Debug] Player "play" event triggered');
                    if (firstPlayRef.current && player.currentTime() < 1) {
                        const duration = Math.floor(player.duration());
                        trackActivity(Math.floor(player.currentTime()), actionType.start, duration);
                        firstPlayRef.current = false;
                    }
                });

                player.on('waiting', () => {
                    console.log('[Debug] Player "waiting" event triggered');
                });

                player.on('canplay', () => {
                    console.log('[Debug] Player "canplay" event triggered');
                });

                player.on('seeking', () => {
                    console.log('[Debug] Player "seeking" event triggered, currentTime:', player.currentTime());
                });

                player.on('seeked', () => {
                    const currentTime = Math.floor(player.currentTime());
                    console.log('[Debug] Player "seeked" event triggered, currentTime:', currentTime);
                    trackActivity(currentTime, actionType.seek);
                });

                player.on('timeupdate', () => {
                    const currentTime = Math.floor(player.currentTime());
                    // Only report if we've moved at least 5 seconds from last reported time
                    if (currentTime > 0 && Math.abs(currentTime - lastReportedTime.current) >= 5) {
                        trackActivity(currentTime, actionType.progress);
                        lastReportedTime.current = currentTime;
                    }
                });

                player.on('pause', () => {
                    console.log('[Debug] Player "pause" event triggered');
                    trackActivity(Math.floor(player.currentTime()), actionType.pause);
                });

                player.on('ended', () => {
                    console.log('[Debug] Player "ended" event triggered');
                    const duration = Math.floor(player.duration());
                    trackActivity(duration, actionType.complete, duration);
                    // Reset firstPlay flag if video ends - next play will be a new start
                    firstPlayRef.current = true;
                    // Update completed status
                    setVideoCompleted(true);
                });

                // Save progress when user leaves the page
                window.addEventListener('beforeunload', () => {
                    if (player && !player.paused()) {
                        trackActivity(Math.floor(player.currentTime()), actionType.pause);
                    }
                });

                // On mobile, try to preload content when the player is ready
                if (isMobileDevice() && resumePosition > 0 && !showOverlay) {
                    try {
                        player.currentTime(resumePosition);
                        console.log('[Debug] Pre-set currentTime for mobile:', resumePosition);
                    } catch (e) {
                        console.error('[Debug] Failed to pre-set currentTime:', e);
                    }
                }
            });
        } else if (playerRef.current) {
            // If we already have a player and options.sources changed, update the source
            const player = playerRef.current;
            if (JSON.stringify(player.src()) !== JSON.stringify(options.sources)) {
                console.log('[Debug] Updating player source');
                player.src(options.sources);
            }
        }
    }, [options, contentId, eventId, isInitialized]);

    // Handler for Resume button click
    const handleResume = () => {
        console.log("[Debug] Resume button clicked");
        const player = playerRef.current;

        if (!player) {
            console.error("[Debug] Player not available for resume");
            return;
        }

        // Hide the overlay first
        setShowOverlay(false);

        // Track resume action
        trackActivity(resumePosition, actionType.resume);

        // Special handling for mobile devices
        if (isMobileDevice()) {
            console.log("[Debug] Mobile resume path");

            // For mobile devices, we'll use a more aggressive approach to ensure playback works
            try {
                // First, try the normal approach
                player.currentTime(resumePosition);
                console.log('[Debug] Set mobile currentTime:', resumePosition);

                // Ensure controls are enabled
                if (player.controlBar && player.controlBar.progressControl) {
                    player.controlBar.progressControl.enable();
                }

                // Play with promise handling
                const playPromise = player.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        console.log('[Debug] Mobile play promise resolved');
                    }).catch(error => {
                        console.error('[Debug] Mobile play failed:', error);
                        // Try with muted as fallback
                        player.muted(true);
                        player.play();
                    });
                }
            } catch (error) {
                console.error('[Debug] Error during mobile resume:', error);
            }
        } else {
            // Desktop path is simpler
            console.log("[Debug] Desktop resume path");
            player.currentTime(resumePosition);
            player.play();
        }
    };

    // Handler for Start from Beginning button click
    const handleStartOver = () => {
        console.log("[Debug] Start from beginning button clicked");
        const player = playerRef.current;

        if (!player) {
            console.error("[Debug] Player not available for start over");
            return;
        }

        // Hide the overlay
        setShowOverlay(false);

        // Reset the first play flag to track this as a new start
        firstPlayRef.current = true;

        if (isMobileDevice()) {
            console.log("[Debug] Mobile start from beginning path");

            try {
                // Set time to beginning
                player.currentTime(0);

                // Ensure controls are enabled
                if (player.controlBar && player.controlBar.progressControl) {
                    player.controlBar.progressControl.enable();
                }

                // Play with promise handling
                const playPromise = player.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        console.log('[Debug] Mobile start from beginning play promise resolved');
                    }).catch(error => {
                        console.error('[Debug] Mobile start from beginning play failed:', error);
                        // Try with muted as fallback
                        player.muted(true);
                        player.play();
                    });
                }
            } catch (error) {
                console.error('[Debug] Error during mobile start from beginning:', error);
            }
        } else {
            // Desktop path
            console.log("[Debug] Desktop start from beginning path");
            player.currentTime(0);
            player.play();
        }
    };

    // Cleanup effect
    React.useEffect(() => {
        return () => {
            const player = playerRef.current;
            if (player && !player.isDisposed()) {
                console.log("[Debug] Cleaning up player");
                if (!player.paused()) {
                    trackActivity(Math.floor(player.currentTime()), actionType.pause);
                }
                player.dispose();
                playerRef.current = null;
            }
        };
    }, []);

    return (
        <div data-vjs-player className="video-player-container">
            <div ref={videoRef} />

            {/* React-based overlay instead of DOM manipulation */}
            {showOverlay && playerRef.current && (
                <div className="vjs-resume-overlay">
                    <div className="resume-buttons-container">
                        {!videoCompleted ? (
                            <>
                                <button
                                    className="resume-button resume-yes"
                                    onClick={handleResume}
                                >
                                    Resume
                                </button>
                                <button
                                    className="resume-button resume-no"
                                    onClick={handleStartOver}
                                >
                                    Start from Beginning
                                </button>
                            </>
                        ) : (
                            <button
                                className="resume-button resume-yes"
                                onClick={handleStartOver}
                            >
                                Watch Again
                            </button>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
                .video-player-container {
                    position: relative;
                    width: 100%;
                }
                
                .vjs-resume-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 2000;
                }
                
                .resume-buttons-container {
                    display: flex;
                    gap: 20px;
                }
                
                .resume-button {
                    font-family: 'DM Sans', sans-serif !important;
                    font-weight: 600 !important;
                    font-size: 16px !important;
                    padding: 14px 32px !important;
                    border-radius: 25px !important;
                    cursor: pointer !important;
                    text-transform: none !important;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.5) !important;
                    opacity: 1 !important;
                }

                .resume-yes {
                    background-color: var(--theme-primary-main, #FF8C00) !important;
                    color: white !important;
                    border: 2px solid var(--theme-primary-dark, #FFA500) !important;
                }

                .resume-no {
                    background-color: #3D3F49 !important; /* Solid gray */
                    color: white !important;
                    border: 2px solid white !important;
                }
                
                /* Increase touch target size for mobile */
                @media (max-width: 768px) {
                    .resume-button {
                        padding: 16px 36px !important;
                        font-size: 18px !important;
                    }
                    
                    .resume-buttons-container {
                        width: 100%;
                        justify-content: space-around;
                        padding: 0 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VideoPlayer;